<script lang="ts">
    import AppStoreIcon from "@components/app-store-icons/AppStoreIcon.svelte"
    import GooglePlayIcon from "@components/app-store-icons/GooglePlayIcon.svelte"
    import Button from "@components/button/Button.svelte"
    import HelpIcon from "@components/help/HelpIcon.svelte"
    import Icon from "@components/material/Icon.svelte"
    import { NativeiOSLink, NativeAndroidLink } from "@utils/native-app-info"
    import { isNative } from "@utils/underhood/browser"
    import { createEventDispatcher } from "svelte"
    import {
        NotificationPermissions,
        requestExactAlarmPermissions,
        requestNotificationPermissions,
    } from "@utils/cap-notification-manager/notification-permission"

    // Granted doesn't matter because the parent deals with that
    export let permissions:
        | NotificationPermissions.DENIED
        | NotificationPermissions.PROMPTABLE
        | NotificationPermissions.UNNOTIFIABLE
        | NotificationPermissions.NEED_EXACT

    const dispatch = createEventDispatcher()
    const exit = () => dispatch("exit")
</script>

<div class="permission-manager">
    {#if permissions === NotificationPermissions.UNNOTIFIABLE}
        <h3 class="warning">
            <Icon icon={"circle-warning"} /> Reminders unsupported
            <HelpIcon path="workbook-reminders#notification-compatibility" />
        </h3>
        {#if isNative}
            <p>
                This native installation should support reminders, but notification support was not
                detected. Please report this if you can: peter@circleofa.org.
            </p>
        {:else}
            <p>
                You are currently using the web version of the app. To access practice reminders,
                please download the mobile app for your iPhone, iPad, or Android device below.
            </p>
        {/if}
        {#if !isNative}
            <div class="store-links">
                <a href={NativeiOSLink} aria-label="Download on the App Store">
                    <AppStoreIcon />
                </a>
                <a href={NativeAndroidLink} aria-label="Get it on Google Play">
                    <GooglePlayIcon />
                </a>
            </div>
        {/if}
        <Button fullwidth icon="close" class="mt-4" color="white" on:click={exit}>Close</Button>
    {:else if permissions === NotificationPermissions.DENIED}
        <h3 class="danger">
            <Icon icon={"circle-danger"} class="w-8 mb-2 inline-block" /> Reminders are off
        </h3>
        <p class="my-2">
            <b
                >This installation supports reminders, but notification permission is currently
                denied.</b
            >
        </p>
        <p class="my-2">
            Enable notifications for this app in your system settings to receive practice reminders.
        </p>
        <Button icon="close" fullwidth color="white" on:click={exit}>Close</Button>
    {:else if permissions === NotificationPermissions.PROMPTABLE}
        <h3 class="info">
            <Icon icon={"circle-info"} class="w-8 mb-2 inline-block" /> Permission needed
        </h3>
        <p class="my-2">
            This installation supports reminders. The app needs notification permission before it
            can schedule them.
        </p>
        <Button
            icon="lock"
            class="mt-2"
            fullwidth
            color="blue"
            on:click={requestNotificationPermissions}>Request permission</Button
        >
    {:else if permissions === NotificationPermissions.NEED_EXACT}
        <h3 class="info">
            <Icon icon={"circle-info"} class="w-8 mb-2 inline-block" /> Exact timing needed
        </h3>
        <p class="my-2">
            To keep reminder timing precise, this app needs exact alarm permission. You may need to
            restart the app after changing this setting.
        </p>
        <Button
            icon="lock"
            class="mt-2"
            fullwidth
            color="blue"
            on:click={requestExactAlarmPermissions}>Open settings</Button
        >
    {/if}
</div>

<style>
    .permission-manager {
        display: grid;
        gap: 1rem;
        color: var(--color-grey-900);
    }

    .permission-manager h3,
    .permission-manager p {
        margin: 0;
    }

    .permission-manager h3 {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.35rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.25;
    }

    .permission-manager h3 :global(svg) {
        width: 1.4rem;
        height: 1.4rem;
    }

    .permission-manager h3.warning :global(svg) {
        color: var(--color-gold-700);
    }

    .permission-manager h3.danger :global(svg) {
        color: var(--danger);
    }

    .permission-manager h3.info :global(svg) {
        color: var(--color-purple-700);
    }

    .permission-manager p {
        color: var(--color-grey-600);
        font-size: 1rem;
        line-height: 1.65;
    }

    .permission-manager p :global(b) {
        color: var(--color-grey-700);
    }

    .store-links {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 1rem;
        width: 100%;
        margin-top: 0.25rem;
    }

    :global(.darkmode) .permission-manager {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .permission-manager h3,
    :global(.darkmode) .permission-manager p :global(b) {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .permission-manager p {
        color: var(--color-grey-200);
    }
</style>
